import type { FC } from 'react';

import classNames from 'classnames';

import { selectKeyframesUpdate } from '../../ducks/search/selectors';
import { useAppSelector } from '../../store/react_redux_typed';
import { ui } from '../../utils/helpers';
import DebugDumpEntity from '../debug_dump_entity/debug_dump_entity';
import CollectionKeyframes from '../keyframes/collection_keyframes';
import { getStateClass } from '../search_items/search_items_pods_list/utils';
import MetadataRow from './metadata_row';
import SearchPodTitle from './search_pod_title';
import type { SearchPodProps } from './types';

import progressStyles from '../search_items/search_item_progress/search_item_progress.module.css';
import styles from './search_pod_collection_detailed.module.css';

const LARGE_POD_THRESHOLD = 308;

const SearchPodCollectionDetailed: FC<SearchPodProps> = (props) => {
  const {
    isDragging,
    isDropTarget,
    searchItem,
    podSize = { height: 146, width: 262 },
    getObjectLink,
    onTitleLinkClick,
    collectionMetadataView,
    hideContextMenu,
    hideFavoriteButton,
    hasPodTitle = true,
  } = props;
  const isDisabled = searchItem.status === 'DELETED';
  const isSelected = Boolean(props.isSelected && !isDisabled);

  const keyframesUpdate = useAppSelector(selectKeyframesUpdate);
  const dragAndDropWaiting = useAppSelector((state) =>
    state.dragAndDropKeyframes.waiting.includes(searchItem.id),
  );
  const waiting =
    keyframesUpdate?.[searchItem.id]?.loading ?? dragAndDropWaiting;
  const childrenUpdate = useAppSelector(
    (state) => state.dragAndDropKeyframes.success[searchItem.id],
  );

  const stateClass = getStateClass({
    isSelected,
    isDragging,
    isDropTarget,
    status: searchItem.status,
  });

  const { metadata } = searchItem;

  const matchingFields = collectionMetadataView?.view_fields || [];

  const isLargePod = podSize && podSize.width >= LARGE_POD_THRESHOLD;

  return (
    <div
      className={stateClass ? styles[stateClass] : undefined}
      draggable="true"
      {...ui('search-pod-collection')}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      aria-label={searchItem.title}
      tabIndex={0}
    >
      <DebugDumpEntity id={searchItem.id} entityKey="search_items" />
      <div
        style={podSize ? { height: `${podSize.height}px` } : undefined}
        className={styles.shape}
      >
        <div>
          <svg
            viewBox="0 0 298 178"
            fill="none"
            stroke="none"
            className={styles.shape_stroke}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.shape_stroke}
              d="M163.109 0H2.00001C0.895437 0 0 0.895431 0 2V180H320V23.8789C320 22.7743 319.105 21.8789 318 21.8789H193.585C192.444 21.4477 188.027 19.4291 182.638 11.8454C178.585 6.13484 173.715 3.17176 169.854 1.63879C168.303 1.01822 166.693 0.551779 165.049 0.246264C164.581 0.1599 164.11 0.0916069 163.636 0.0415385C163.474 0.0248535 163.311 0.0116637 163.148 0.00197802L163.12 0.000989011L163.109 0Z"
            />
          </svg>
        </div>
        <CollectionKeyframes
          childrenUpdate={childrenUpdate}
          className={classNames(styles.keyframes, {
            [styles.keyframes_large]: isLargePod,
          })}
          customKeyframeClassName={classNames(styles.custom_keyframes, {
            [styles.custom_keyframes_large]: isLargePod,
          })}
          keyframes={searchItem.keyframes ?? undefined}
          waiting={waiting}
        />
        {waiting && (
          <div
            role="progressbar"
            className={classNames(
              progressStyles.progress_line,
              'WAITING',
              styles.waiting,
              { [styles.waiting_large_pod]: isLargePod },
            )}
            tabIndex={-1}
          />
        )}
      </div>
      {hasPodTitle && (
        <div className={styles.details_container}>
          <div className={styles.title_container}>
            <SearchPodTitle
              searchItem={searchItem}
              objectType="collection"
              isSelected={isSelected}
              isDropTarget={isDropTarget}
              getObjectLink={getObjectLink}
              onTitleLinkClick={onTitleLinkClick}
              hideContextMenu={hideContextMenu}
              hideFavouriteButton={hideFavoriteButton}
            />
          </div>
          {isLargePod && matchingFields.length > 0 ? (
            <div className={styles.metadata_container}>
              {matchingFields.map((item, i) => {
                const value = metadata?.[item.name];

                return <MetadataRow key={i} field={item} value={value} />;
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchPodCollectionDetailed;
